import { useCallback, useEffect, useState } from "react";

/** Position the tooltip near the highlighted element, clamped to stay on-screen. */
export function computeTourTooltipStyle(rect) {
  const TOOLTIP_WIDTH = 320;
  const MARGIN = 16;
  let top = rect.bottom + 14;
  if (top + 200 > window.innerHeight) top = Math.max(rect.top - 210, MARGIN);
  let left = rect.left;
  if (left + TOOLTIP_WIDTH + MARGIN > window.innerWidth) left = window.innerWidth - TOOLTIP_WIDTH - MARGIN;
  left = Math.max(left, MARGIN);
  return { top, left };
}

/**
 * Drives a simple guided walkthrough: highlights `steps[i].selector` one at a
 * time. Steps whose element isn't in the DOM are skipped automatically.
 * `autoStartKey`, when given, auto-runs the tour once per browser the first
 * time this hook mounts (localStorage-backed) unless `skipAutoStart` is set.
 */
export function useGuidedTour({ steps, autoStartKey, skipAutoStart, autoStartDelay = 700 }) {
  const [tourRunning, setTourRunning] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourRect, setTourRect] = useState(null);

  const endTour = useCallback(() => {
    setTourRunning(false);
    setTourRect(null);
    if (autoStartKey) {
      try {
        localStorage.setItem(autoStartKey, "1");
      } catch (_) {
        /* ignore */
      }
    }
  }, [autoStartKey]);

  const startTour = useCallback(() => {
    setTourStep(0);
    setTourRunning(true);
  }, []);

  // Auto-start once per browser, the first time this hook mounts.
  useEffect(() => {
    if (!autoStartKey || skipAutoStart) return undefined;
    try {
      if (!localStorage.getItem(autoStartKey)) {
        const t = setTimeout(() => {
          setTourStep(0);
          setTourRunning(true);
        }, autoStartDelay);
        return () => clearTimeout(t);
      }
    } catch (_) {
      /* ignore */
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStartKey, skipAutoStart]);

  // Locate and measure the current step's target element. Steps whose
  // element isn't in the DOM are skipped forward automatically.
  useEffect(() => {
    if (!tourRunning) return undefined;
    const step = steps[tourStep];
    if (!step) {
      endTour();
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      const el = document.querySelector(step.selector);
      if (!el) {
        setTourStep((i) => i + 1);
        return;
      }
      el.scrollIntoView({ block: "center", behavior: "auto" });
      requestAnimationFrame(() => {
        if (!cancelled) setTourRect(el.getBoundingClientRect());
      });
    }, 60);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourRunning, tourStep, steps]);

  // Keep the highlight aligned with its target if the viewport changes size.
  useEffect(() => {
    if (!tourRunning) return undefined;
    const step = steps[tourStep];
    if (!step) return undefined;
    const onResize = () => {
      const el = document.querySelector(step.selector);
      if (el) setTourRect(el.getBoundingClientRect());
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourRunning, tourStep]);

  return { tourRunning, tourStep, tourRect, startTour, endTour, setTourStep, setTourRunning, setTourRect };
}

/** Renders the highlight box + tooltip for the current step of a `useGuidedTour`. */
export function GuidedTourOverlay({ steps, tourRunning, tourStep, tourRect, onSkip, onNext, nextLabel }) {
  const step = steps[tourStep];
  if (!tourRunning || !step) return null;
  return (
    <div className="admin-tour-overlay">
      <div
        className={`admin-tour-highlight${tourRect ? "" : " admin-tour-highlight--center"}`}
        style={
          tourRect
            ? {
                top: tourRect.top - 8,
                left: tourRect.left - 8,
                width: tourRect.width + 16,
                height: tourRect.height + 16,
              }
            : undefined
        }
      />
      <div
        className="admin-tour-tooltip"
        style={
          tourRect
            ? computeTourTooltipStyle(tourRect)
            : { top: "40%", left: "50%", transform: "translate(-50%, -50%)" }
        }
      >
        <div className="admin-tour-step-count">
          Step {tourStep + 1} of {steps.length}
        </div>
        <h4 className="admin-tour-title">{step.title}</h4>
        <p className="admin-tour-description">{step.description}</p>
        <div className="admin-tour-actions">
          <button type="button" className="admin-tour-btn admin-tour-btn-skip" onClick={onSkip}>
            Skip
          </button>
          <button type="button" className="admin-tour-btn admin-tour-btn-next" onClick={onNext}>
            {nextLabel ?? (tourStep >= steps.length - 1 ? "Finish" : "Next")}
          </button>
        </div>
      </div>
    </div>
  );
}
